import React, { useEffect, useMemo, useState } from 'react';
import apis from '../../../../Api';
import { Modal, Button, Table, Input, InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap';
import ClipLoader from 'react-spinners/ClipLoader';
import { connect } from 'react-redux';
import './AllCoursesModal.css';

const BRAND_COLORS = {
  primary: '#0e1d3f'
};

export function AllCoursesModal(props){
  const { isOpen, toggle, loading, allCourses, onOpenUnits, onOpenDetails, onBack, translate = {} } = props;
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [sortOption, setSortOption] = useState('default');
  const pageSize = 50;
  // cache de quantidade de turmas por curso
  const [courseClassCounts, setCourseClassCounts] = useState({}); // { [courseId]: { count:number, loading:boolean, error?:boolean } }
  // internal state for courses when modal manages fetching
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalCourses, setInternalCourses] = useState([]);

  // totais para o cabeçalho
  const totals = useMemo(() => {
    const base = (allCourses && allCourses.length) ? allCourses : internalCourses;
    const totalCourses = (base || []).length;
    const totalUnits = (base || []).reduce((s, c) => s + (Number(c.unitsCount || 0)), 0);
    const totalStudents = (base || []).reduce((s, c) => s + (Number(c.studentsTotal || 0)), 0);
    return { totalCourses, totalUnits, totalStudents };
  }, [allCourses, internalCourses]);

  const filteredCourses = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase();
    const base = (allCourses && allCourses.length) ? allCourses : internalCourses;
    if (!term) return base;
    return base.filter(c => {
      const name = (c.name || '').toLowerCase();
      const idStr = String(c.id || '');
      return name.includes(term) || idStr.includes(term);
    });
  }, [searchTerm, allCourses, internalCourses]);

  const sortedCourses = useMemo(() => {
    const list = [...(filteredCourses || [])];
    const getUnits = (course) => Number(course.unitsCount || 0);
    const getStudents = (course) => Number(course.studentsTotal || 0);
    const getClasses = (course) => {
      const info = courseClassCounts[course.id] || courseClassCounts[Number(course.id)] || courseClassCounts[String(course.id)];
      if (info && typeof info.count === 'number') return info.count;
      if (course.classesCount !== undefined) return Number(course.classesCount) || 0;
      if (course.classCount !== undefined) return Number(course.classCount) || 0;
      if (course.turmasCount !== undefined) return Number(course.turmasCount) || 0;
      if (course.turmasTotal !== undefined) return Number(course.turmasTotal) || 0;
      return 0;
    };
    switch (sortOption) {
      case 'studentsDesc':
        list.sort((a, b) => getStudents(b) - getStudents(a));
        break;
      case 'studentsAsc':
        list.sort((a, b) => getStudents(a) - getStudents(b));
        break;
      case 'unitsDesc':
        list.sort((a, b) => getUnits(b) - getUnits(a));
        break;
      case 'unitsAsc':
        list.sort((a, b) => getUnits(a) - getUnits(b));
        break;
      case 'classesDesc':
        list.sort((a, b) => getClasses(b) - getClasses(a));
        break;
      case 'classesAsc':
        list.sort((a, b) => getClasses(a) - getClasses(b));
        break;
      default:
        break;
    }
    return list;
  }, [filteredCourses, sortOption, courseClassCounts]);

  const totalPages = useMemo(() => {
    const total = (sortedCourses || []).length;
    return Math.max(1, Math.ceil((total || 0) / pageSize));
  }, [sortedCourses, pageSize]);

  // resetar para a primeira página ao trocar a busca
  useEffect(() => { setPage(1); }, [searchTerm, sortOption]);

  // garantir que a página atual esteja em um intervalo válido
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [totalPages]);

  // itens da página atual
  const paginatedCourses = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (sortedCourses || []).slice(start, start + pageSize);
  }, [sortedCourses, page, pageSize]);

  // Carrega contagem de turmas para os cursos visíveis (lazy)
  useEffect(() => {
    if (!isOpen) return;
    const toFetch = paginatedCourses
      .map(c => c.id)
      .filter(id => {
        const num = parseInt(id, 10);
        if (Number.isNaN(num)) return false; // ignora IDs inválidos
        return !courseClassCounts[num];
      });
    if (toFetch.length === 0) return;
    toFetch.forEach(id => {
      const num = parseInt(id, 10);
      if (Number.isNaN(num)) return; // segurança extra
      setCourseClassCounts(prev => ({ ...prev, [num]: { count: 0, loading: true } }));
      apis.getCourseClasses(num)
        .then(res => {
          const list = Array.isArray(res.data) ? res.data : (res.data?.classes || []);
          setCourseClassCounts(prev => ({ ...prev, [num]: { count: list.length, loading: false } }));
        })
        .catch(() => {
          setCourseClassCounts(prev => ({ ...prev, [num]: { count: 0, loading: false, error: true } }));
        });
    });
  }, [paginatedCourses, isOpen, courseClassCounts]);

  useEffect(() => {
    let mounted = true;
    const loadAllCourses = async () => {
      if (allCourses && allCourses.length) return;
      setInternalLoading(true);
      try {
        let rows = [];
        try {
          const rptResp = await apis.getCoursesUnitsReport();
          if (rptResp && Array.isArray(rptResp.data)) rows = rptResp.data;
        } catch (e) {
          const classroomsResp = await apis.getAllClassRoom();
          const classrooms = (classroomsResp && Array.isArray(classroomsResp.data)) ? classroomsResp.data : [];
          const courseMap = {};
          classrooms.forEach(cr => {
            const courseId = cr.course;
            const companyId = cr.company;
            if (!courseId || !companyId) return;
            const key = String(courseId);
            if (!courseMap[key]) courseMap[key] = { id: courseId, name: "", companyIds: new Set(), companyEntries: [], studentsTotal: 0 };
            if (!courseMap[key].companyIds.has(companyId)) {
              courseMap[key].companyIds.add(companyId);
              const companyNameFromClass = cr.companyName || cr.company_name || cr.companyNameFormatted;
              courseMap[key].companyEntries.push({ id: companyId, name: companyNameFromClass || `Unidade #${companyId}` });
            }
            const studentsCount = Number(cr.countStudents || cr.countParticipants || cr.countParticipantsTotal || cr.count || 0);
            courseMap[key].studentsTotal += studentsCount;
          });
          rows = Object.values(courseMap).map(r => ({ course: r.id, unitsCount: r.companyIds.size, companies: r.companyEntries, studentsTotal: r.studentsTotal }));
        }

        const built = rows.map(r => {
          let companies = [];
          if (r.companies && Array.isArray(r.companies)) companies = r.companies;
          else if (r.companiesList && typeof r.companiesList === 'string' && r.companiesList.length) {
            companies = r.companiesList.split(',').map(pair => {
              const [idRaw, ...nameParts] = pair.split(':');
              const id = idRaw ? idRaw.trim() : idRaw;
              const nameRaw = nameParts.join(':').trim();
              return { id: id, name: nameRaw };
            });
          }
          return {
            id: r.course,
            name: r.courseName || `Curso #${r.course}`,
            unitsCount: r.unitsCount || 0,
            companies,
            studentsTotal: r.studentsTotal || 0
          };
        });
        if (mounted) setInternalCourses(built);
      } catch (err) {
        console.error('Erro ao montar relatório de cursos (AllCoursesModal):', err);
      } finally {
        if (mounted) setInternalLoading(false);
      }
    };
    if (isOpen) loadAllCourses();
    return () => { mounted = false; };
  }, [isOpen, allCourses]);

  // números da paginação com reticências
  const pageNumbers = useMemo(() => {
    const tp = totalPages;
    const cur = page;
    const arr = [];
    const pushRange = (s, e) => { for (let i = s; i <= e; i++) arr.push(i); };
    if (tp <= 7) {
      pushRange(1, tp);
    } else {
      arr.push(1);
      if (cur > 3) arr.push('...');
      const start = Math.max(2, cur - 1);
      const end = Math.min(tp - 1, cur + 1);
      pushRange(start, end);
      if (cur < tp - 2) arr.push('...');
      arr.push(tp);
    }
    return arr;
  }, [totalPages, page]);

  // decide qual fonte de cursos usar e se estamos carregando
  const baseCourses = (allCourses && allCourses.length) ? allCourses : internalCourses;
  const isLoading = Boolean(loading) || Boolean(internalLoading);

  return (
    <Modal
      className="modal-dialog-centered"
      size="xl"
      isOpen={isOpen}
  toggle={toggle}
  contentClassName="all-courses-modal"
  style={{ maxWidth: '95%', maxHeight: '90vh' }}
    >
      <div className="modal-header d-flex align-items-center">
        <h5 className="modal-title mb-0 text-white">{translate['relatorio_cursos'] || 'Relatório de Cursos — Unidades por Curso'}</h5>
        <button aria-label="Close" className="close text-white" data-dismiss="modal" type="button" onClick={toggle}>
          <span aria-hidden={true}>×</span>
        </button>
      </div>
      <div className="modal-body" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        {isLoading ? (
          <div className="text-center py-4">
            <ClipLoader size={36} color={BRAND_COLORS.primary} loading={true} />
            <p className="mt-2">{translate['gerando_relatorio'] || 'Gerando relatório...'}</p>
          </div>
        ) : (baseCourses || []).length === 0 ? (
          <div className="text-center text-muted py-4">{translate['nenhum_curso_encontrado'] || 'Nenhum curso encontrado.'}</div>
        ) : (
          <div className="table-responsive">
            <div className="toolbar px-2 mb-3">
              <div className="toolbar-left">
                <InputGroup className="search-group">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText className="search-icon"><i className="fas fa-search" /></InputGroupText>
                  </InputGroupAddon>
                  <Input
                    className="search-input"
                    placeholder={translate['buscar_curso_por_nome_ou_id'] || 'Buscar curso por nome ou ID...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value || '')}
                  />
                </InputGroup>
              </div>
              <div className="toolbar-right">
                <div className="sort-wrapper">
                  <div className="sort-label">{translate['ordenar_por'] || 'Ordenar por'}</div>
                  <Input
                    type="select"
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    className="sort-select"
                  >
                    <option value="default">{translate['ordenacao_padrao'] || 'Ordenação padrão'}</option>
                    <option value="studentsDesc">{translate['mais_alunos'] || 'Mais alunos'}</option>
                    <option value="studentsAsc">{translate['menos_alunos'] || 'Menos alunos'}</option>
                    <option value="unitsDesc">{translate['mais_unidades'] || 'Mais unidades'}</option>
                    <option value="unitsAsc">{translate['menos_unidades'] || 'Menos unidades'}</option>
                    <option value="classesDesc">{translate['mais_turmas'] || 'Mais turmas'}</option>
                    <option value="classesAsc">{translate['menos_turmas'] || 'Menos turmas'}</option>
                  </Input>
                </div>
                <div className="toolbar-stats small text-muted d-none d-md-flex">
                  <span>Cursos: <strong className="text-dark">{totals.totalCourses}</strong></span>
                  <span>Unidades: <strong className="text-dark">{totals.totalUnits}</strong></span>
                  <span>Alunos: <strong className="text-dark">{totals.totalStudents}</strong></span>
                </div>
              </div>
            </div>
            <Table className="align-items-center table-hover">
              <thead className="thead-light">
                <tr>
                  <th style={{minWidth: '40%'}}>{translate['curso'] || 'Curso'}</th>
                  <th className="text-center" style={{width: 160}}>{translate['unidades'] || 'Unidades'}</th>
                  <th className="text-center" style={{width: 120}}>{translate['turmas'] || 'Turmas'}</th>
                  <th className="text-center" style={{width: 120}}>{translate['alunos'] || 'Alunos'}</th>
                  <th className="text-center" style={{width: 120}}>{translate['acoes'] || 'Ações'}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCourses.map(c => (
                  <tr key={`allcourse-${c.id}`} style={{cursor: 'default'}}>
                    <td>
                      <div className="font-weight-bold">{c.name}</div>
                      <div className="text-muted small">ID: {c.id}</div>
                    </td>
                    <td className="text-center">
                      <div className="d-flex flex-column align-items-center">
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          className="d-inline-flex align-items-center table-action-btn"
                          onClick={() => onOpenUnits && onOpenUnits(c)}
                        >
                          <i className="fas fa-building mr-1" /> {c.unitsCount || 0}
                        </Button>
                        {Array.isArray(c.companies) && c.companies.length > 0 && (
                          <div className="text-muted small mt-1">{c.companies.slice(0,2).map(x => x.name).join(', ')}{c.companies.length > 2 ? '...' : ''}</div>
                        )}
                      </div>
                    </td>
                    <td className="text-center align-middle">
                      {(() => {
                        const info = courseClassCounts[c.id];
                        if (!info) return <span className="text-muted">…</span>;
                        if (info.loading) return <span className="text-muted"><i className="fas fa-spinner fa-spin" /></span>;
                        if (info.error) return <span title="Erro" className="text-danger">-</span>;
                        return info.count;
                      })()}
                    </td>
                    <td className="text-center align-middle">
                      {c.studentsTotal || 0}
                    </td>
                        <td className="text-center align-middle">
                        <Button
                          size="sm"
                          color="primary"
                          className="d-inline-flex align-items-center table-action-btn"
                          onClick={() => {
                          const num = parseInt(c.id, 10);
                          if (Number.isNaN(num)) return; // não abrir se inválido
                          // envia objeto mínimo; CourseDetailModal fará auto-fetch
                          onOpenDetails && onOpenDetails({ id: num, name: c.name });
                        }}
                        >
                        <i className="fas fa-eye mr-1" /> {translate['detalhes'] || 'Detalhes'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {(sortedCourses.length > pageSize) && (
              <div className="d-flex justify-content-between align-items-center px-2 py-2">
                <div className="text-muted small">
                  {translate['pagina_de'] ? translate['pagina_de'].replace('{page}', page).replace('{totalPages}', totalPages) : `Página ${page} de ${totalPages}`} — {Math.min(sortedCourses.length, (page - 1) * pageSize + 1)}–{Math.min(sortedCourses.length, page * pageSize)} de {sortedCourses.length}
                </div>
                <div className="d-flex align-items-center">
                  <Button
                    size="sm"
                    color="primary"
                    outline
                    className="mr-2 pagination-nav-btn"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    {translate['anterior'] || 'Anterior'}
                  </Button>
                  {pageNumbers.map((p, idx) => (
                    p === '...'
                      ? <span key={`ellipsis-${idx}`} className="mx-1 text-muted">…</span>
                      : (
                        <Button
                          key={`p-${p}`}
                          size="sm"
                          color="primary"
                          outline={p !== page}
                          className={`mx-1 pagination-page-btn ${p === page ? 'active' : ''}`}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </Button>
                      )
                  ))}
                  <Button
                    size="sm"
                    color="primary"
                    outline
                    className="ml-2 pagination-nav-btn"
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    {translate['proxima'] || 'Próxima'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="modal-footer d-flex justify-content-between align-items-center w-100">
        <div>
          {onBack && (
            <Button
              color="primary"
              outline
              size="sm"
              className="d-inline-flex align-items-center footer-btn"
              onClick={onBack}
            >
              <i className="fas fa-arrow-left mr-1" />
              {translate['voltar'] || 'Voltar'}
            </Button>
          )}
        </div>
        <div>
          <Button
            color="primary"
            size="sm"
            className="d-inline-flex align-items-center footer-btn"
            onClick={toggle}
          >
            <i className="fas fa-times mr-1" />
            {translate['fechar'] || 'Fechar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default connect(state => ({ translate: state.translate }))(AllCoursesModal);